import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import FadeUp from "../components/FadeUp";
import { useLang, dictFull } from "../i18n";
import { api } from "../lib/api";

const CATS = ["bridal", "evening", "suits", "mother"];

export default function Collection() {
  const { t, lang } = useLang();
  const [dresses, setDresses] = useState([]);
  const [meta, setMeta] = useState({ designers: [], silhouettes: [], fabrics: [] });
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dresses").then((r) => {
      setDresses(r.data);
      setMeta({
        designers: [...new Set(r.data.map((d) => d.designer).filter(Boolean))].sort(),
        silhouettes: [...new Set(r.data.map((d) => d.silhouette).filter(Boolean))].sort(),
        fabrics: [...new Set(r.data.map((d) => d.fabric).filter(Boolean))].sort(),
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && next.get(key) !== value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setParams(next, { replace: true });
  };
  const setParamsMulti = (pairs) => {
    const next = new URLSearchParams(params);
    pairs.forEach(([k, v]) => {
      if (v && next.get(k) !== v) next.set(k, v);
      else next.delete(k);
    });
    next.delete("page");
    setParams(next, { replace: true });
  };

  const [barCollapsed, setBarCollapsed] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setBarCollapsed(y > 300 && y > lastY.current);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const filters = {
    silhouette: params.get("silhouette") || "",
    fabric: params.get("fabric") || "",
    category: params.get("category") || "",
    collection: params.get("collection") || "",
  };

  const filtered = useMemo(
    () => dresses.filter((d) =>
      (!filters.silhouette || d.silhouette === filters.silhouette) &&
      (!filters.fabric || d.fabric === filters.fabric) &&
      (!filters.category || d.category === filters.category) &&
      (!filters.collection || (d.category === "bridal" && d.collection === filters.collection))
    ),
    [dresses, params]
  );

  const FilterRow = ({ keyName, options, testid }) => (
    <div className="flex flex-wrap items-center gap-2" data-testid={`filter-row-${keyName}`}>
      <span className="eyebrow mr-2">{t(`collection.${keyName}`)}</span>
      <button onClick={() => setParam(keyName, "")} data-testid={`filter-${keyName}-all`}
        className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase rounded-full border transition-colors ${!filters[keyName] ? "border-charcoal bg-charcoal text-ivory" : "border-softline text-stone hover:border-charcoal"}`}>
        {t("collection.all")}
      </button>
      {options.map((o) => (
        <button key={o} onClick={() => setParam(keyName, o)} data-testid={`filter-${keyName}-${o.replace(/\s+/g, "-").toLowerCase()}`}
          className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase rounded-full border transition-colors ${filters[keyName] === o ? "border-charcoal bg-charcoal text-ivory" : "border-softline text-stone hover:border-charcoal"}`}>
          {(keyName === "silhouette" ? dictFull.silhouettes : dictFull.fabrics)[o]?.[lang] || o}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="collection-page">
      <section className="pt-40 pb-12 lg:pt-52 max-w-[1440px] mx-auto px-6 lg:px-12">
        <FadeUp><h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl">{t("collection.title")}</h1></FadeUp>
        <FadeUp delay={0.1}><p className="mt-6 max-w-xl text-[15px] leading-relaxed text-stone">{t("collection.sub")}</p></FadeUp>
      </section>

      <section className={`sticky top-[76px] z-30 bg-[#FDFBF7]/90 backdrop-blur-md border-y border-softline transition-transform duration-500 ${barCollapsed ? "-translate-y-full" : "translate-y-0"}`} data-testid="collection-filters">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow mr-2">{t("collection.category")}</span>
            {["", "bridal", "evening", "suits"].map((cat) => {
              const isActive = filters.category === cat && !filters.collection;
              return (
                <button key={cat || "all"} onClick={() => setParamsMulti([["category", cat], ["collection", ""], ["silhouette", ""], ["fabric", ""]])}
                  data-testid={`filter-category-${cat || "all"}`}
                  className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase rounded-full border transition-colors ${isActive ? "border-charcoal bg-charcoal text-ivory" : "border-softline text-stone hover:border-charcoal"}`}>
                  {cat === "" ? t("collection.all") : t(`categories.${cat}`)}
                </button>
              );
            })}
            {filters.category === "bridal" && ["selected", "couture"].map((coll) => (
              <button key={coll} onClick={() => setParamsMulti([["category", "bridal"], ["collection", filters.collection === coll ? "" : coll]])}
                data-testid={`filter-collection-${coll}`}
                className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase rounded-full border transition-colors ${filters.collection === coll ? "border-gold bg-gold text-charcoal" : "border-champagne text-stone hover:border-gold"}`}>
                {t(`collections.${coll}`)}
              </button>
            ))}
          </div>
          {filters.category === "bridal" && (
            <div className="space-y-3 pt-2 border-t border-softline/60">
              <FilterRow keyName="silhouette" options={meta.silhouettes} />
              <FilterRow keyName="fabric" options={meta.fabrics} />
            </div>
          )}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="flex justify-between items-baseline mb-10">
          <p className="text-sm text-muted" data-testid="collection-count">{filtered.length} {lang === "it" ? "abiti" : lang === "en" ? "dresses" : "件"}</p>
          {(filters.silhouette || filters.fabric || filters.category || filters.collection) && (
            <button className="btn-line !text-[11px]" data-testid="collection-reset" onClick={() => setParams({}, { replace: true })}>
              {t("collection.reset")}
            </button>
          )}
        </div>
        {(() => {
          const PAGE_SIZE = 12;
          const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
          const currentPage = Math.min(Math.max(1, parseInt(params.get("page") || "1")), totalPages);
          const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
          const setPage = (p) => {
            const next = new URLSearchParams(params);
            if (p > 1) next.set("page", String(p)); else next.delete("page");
            setParams(next, { replace: true });
            window.scrollTo({ top: 0, behavior: "instant" });
          };
          return (
            <>
              {loading ? (
                <p className="text-muted text-sm">…</p>
              ) : filtered.length === 0 ? (
                <p className="font-serif text-2xl text-stone py-20" data-testid="collection-empty">{t("collection.empty")}</p>
              ) : (
                <div className="grid gap-x-6 gap-y-12 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                  <AnimatePresence>
                    {pageItems.map((d, i) => (
                      <motion.div key={d.id} layout initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: Math.min(i % 6, 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}>
                        <Link to={`/collezione/${d.id}`} data-testid={`dress-card-${d.code}`} className="block group">
                          <div className="clip-frame aspect-[3/4] border border-softline relative">
                            <img src={d.images?.[0]} alt={d.name} className="w-full h-full object-cover spotlight-img" loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            {d.category === "bridal" && (
                              <span className={`absolute top-4 right-4 text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 ${d.collection === "couture" ? "bg-charcoal/80 text-gold" : "bg-ivory/90 text-charcoal border border-softline"}`}>
                                {t(`collections.${d.collection}`)}
                              </span>
                            )}
                            {d.status !== "available" && (
                              <span className="absolute top-4 left-4 bg-charcoal/80 text-ivory text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">{d.status}</span>
                            )}
                          </div>
                          <div className="pt-4 flex justify-between items-baseline gap-3">
                            <div>
                              <p className="font-serif text-xl leading-tight">{d.name}</p>
                              <p className="text-[11px] tracking-[0.18em] uppercase text-muted mt-1">{d.fabric || ""}</p>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
              {totalPages > 1 && (
                <nav className="mt-16 flex items-center justify-center gap-2" data-testid="collection-pagination" aria-label="Pagination">
                  <button onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} data-testid="page-prev"
                    className="px-4 py-2 text-[11px] tracking-[0.18em] uppercase border border-softline rounded-full text-stone hover:border-charcoal disabled:opacity-30 transition-colors">‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button key={p} onClick={() => setPage(p)} data-testid={`page-${p}`}
                      className={`w-10 h-10 rounded-full text-[12px] tracking-wide transition-colors ${p === currentPage ? "bg-charcoal text-ivory" : "border border-softline text-stone hover:border-charcoal"}`}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} data-testid="page-next"
                    className="px-4 py-2 text-[11px] tracking-[0.18em] uppercase border border-softline rounded-full text-stone hover:border-charcoal disabled:opacity-30 transition-colors">›</button>
                </nav>
              )}
            </>
          );
        })()}
      </section>
    </div>
  );
}
